import { AppImages } from '@/config'
import { Channel, StreamChat, UserResponse } from 'stream-chat'
import { ChatLocalMesage, forceChatRerender, getChatUnreadCount, isChatGroup } from '@packages/chat'
import { getDisplayTitle, getDisplayImage, getWholeChar } from 'stream-chat-react'

export const getChannelDisplayData = (channel: Channel, user: UserResponse) => {
  // From: https://github.com/GetStream/stream-chat-react/blob/master/src/components/Avatar/Avatar.tsx

  const channelName = getDisplayTitle(channel, user)
  const channelImage = getDisplayImage(channel, user)
  const nameInitials = getWholeChar(channelName, 0)

  const icon = channelImage ?? `https://getstream.io/random_png/?name=${nameInitials}&size=96`

  return {
    title: channelName,
    icon,
  }
}

export const getMentionPreference = (channel: Channel, userId: string, cid?: string) => {
  const resolvedCid = cid || channel?.cid
  const storageKey = resolvedCid && userId ? `mention_notifications:${resolvedCid}:${userId}` : ''
  const stored = typeof window !== 'undefined' && storageKey ? window.localStorage.getItem(storageKey) : null
  const channelKey = userId ? `mention_notifications_${userId}` : ''

  return stored || (channel?.data as any)?.[channelKey] || 'all'
}

export const hasMention = (message: any, event: any, currentUid: string) => {
  const mentionedUsers = message?.mentioned_users || event?.mentioned_users || []

  return mentionedUsers?.some((u: any) => {
    const id = typeof u === 'string' ? u : u?.id || u?.user_id
    return id === currentUid
  })
}

export const isThreadParticipant = async (
  client: StreamChat,
  message: ChatLocalMesage,
  currentUserId: string,
): Promise<boolean> => {
  if (!message?.parent_id) {
    return true
  }
  try {
    const parentMessageResponse = await client.getMessage(message.parent_id)
    const parentMessage = parentMessageResponse?.message

    if (!parentMessage) {
      return false
    }

    const threadParticipants = (parentMessage as any)?.thread_participants || []
    const parentUserId = parentMessage?.user?.id
    const isStarter = parentUserId === currentUserId
    const isParticipant = isStarter || threadParticipants.some((participant: any) => {
      const participantId = typeof participant === 'string' ? participant : participant?.id || participant?.user_id
      return participantId === currentUserId
    })
    return isParticipant
  } catch (e) {
    return true
  }
}

export function createMuteTimer(channelId: string, channelType: string, expiresAt: string, activeListenerInstance) {
  if (!activeListenerInstance?.client) {
    console.warn('createMuteTimer called but no active client instance')
    return
  }

  const { client, muteTimers } = activeListenerInstance

  const expirationTime = new Date(expiresAt).getTime()
  const now = Date.now()
  const timeUntilExpiration = expirationTime - now

  if (timeUntilExpiration > 0) {
    if (muteTimers.has(channelId)) {
      clearTimeout(muteTimers.get(channelId))
    }

    const timeout = setTimeout(async () => {
      // Check if client still exists
      if (!activeListenerInstance?.client) {
        console.warn('Timer fired but client is no longer available')
        return
      }

      try {
        const channel = client.channel(channelType, channelId)

        await channel.watch()

        if (channelType === 'team') {
          return
        }

        const updatedMutes = (client.mutedChannels || []).filter(
          (mute: any) => mute.channel?.cid !== channel.cid,
        )
        client.mutedChannels = updatedMutes

        const unreadCount = getChatUnreadCount(channel)
        if (unreadCount > 0) {
          channel.state.unreadCount = unreadCount
        }

        client.dispatchEvent({
          type: 'channel.mutes_updated',
          channel,
          cid: channel.cid,
          me: { ...client.user, channel_mutes: updatedMutes },
        })

        client.dispatchEvent({
          type: 'channel.updated',
          channel,
          cid: channel.cid,
        })

        client.dispatchEvent({
          type: 'notification.channel_mutes_updated',
          channel,
          cid: channel.cid,
          me: client.user,
        })

        forceChatRerender(channelId)
      } catch (error) {
        console.error(`Error syncing channel ${channelId} (type: ${channelType}):`, error)
      }

      muteTimers.delete(channelId)
    }, timeUntilExpiration)

    muteTimers.set(channelId, timeout)
  }
}

export function buildMessageNotificationData(event: any, channel: Channel, client: StreamChat): NotificationOptions & { title: string } {
  const user = client.user
  const isGroup = isChatGroup(channel)
  const message = event.message as ChatLocalMesage
  const reaction = event.reaction

  let body = message?.customMessage
  const senderUser = message?.user
  const title = senderUser?.name
  const mentioned_users = message.mentioned_users || []

  const displayData = getChannelDisplayData(channel, user)
  const { title: channelName, icon } = displayData

  const isMentioned = mentioned_users?.some(m => m.id === user?.id)

  if (isGroup) {
    body = `${channelName}\n${message?.customMessage}`

    if (isMentioned) {
      body = `Mentioned you - ${channelName}\n${message?.customMessage}`
    }
  }

  if (reaction) {
    body = `Reacted to ${reaction?.emoji_code} "${message.customMessage}"`
  }

  return {
    title,
    body,
    tag: `${event.type}:${message.id}:${channel.id}:${new Date().getTime()}`,
    icon,
    badge: AppImages.NotificationIcon.src,
    data: {
      messageId: message?.id,
      parentId: message?.parent_id,
      channelId: channel?.id,
      channelType: event?.channel_type || channel?.type,
      receiver: user?.id,
    },
  }
}
